import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { localStorageHelper } from '../../../../utils/localStorageHelper'

export const DEFAULT_LABELS = {
  subTitle: '',
  allItems: 'All Items',
  allMyItems: 'My Items',
  multipleItems: 'Multiple'
}

export const DenaliScopeContext = createContext({
  scopeKey: '',
  scopes: [],
  selectedValues: [],
  selectScopeKey: () => {},
  selectScopeValues: () => {}
})

export function useDenaliScopes () {
  return useContext(DenaliScopeContext)
}

export const useSelectedDenaliScope = () => {
  const { scopeKey, scopes, selectedValues } = useContext(DenaliScopeContext)
  return useMemo(() => {
    const scope = scopes.find(x => x.name === scopeKey) ?? (scopes.length ? scopes.at(0) : null)
    const values = selectedValues

    return { scope, values }
  }, [scopeKey, scopes, selectedValues])
}

export function DenaliScopeProvider ({ storageKey, scopes, children }) {
  const _scopes = useMemo(() => {
    return (scopes || []).map(scope => ({
      ...scope,
      labels: {
        ...DEFAULT_LABELS,
        ...(scope.labels || {})
      }
    }))
  }, [scopes])

  /* Create default scope keys to prevent from checking local storage constantly */
  const [defaultScopeKey, defaultScopeValues] = useMemo(() => {
    return [
      localStorageHelper.tryLoad(`dascopekey_${storageKey}`) || (_scopes.length ? _scopes.at(0)?.name : null),
      localStorageHelper.tryLoad(`dascopevalues_${storageKey}`) || []
    ]
  }, [storageKey, _scopes])

  // Consider combining these to be atomically changeable //
  /* Getter and setter for current scope values */
  const [currentScopeValues, setCurrentScopeValues] = useState(defaultScopeValues)
  const selectScopeValues = useCallback((scopeValues = []) => {
    setCurrentScopeValues(() => {
      localStorageHelper.tryStore(`dascopevalues_${storageKey}`, scopeValues)
      return scopeValues
    })
  }, [setCurrentScopeValues, storageKey])

  /* Getter and setter for currently selected scope */
  const [currentScopeKey, setCurrentScopeKey] = useState(defaultScopeKey)
  const selectScopeKey = useCallback((scopeKey) => {
    setCurrentScopeKey(prev => {
      if (prev === scopeKey) return prev

      localStorageHelper.tryStore(`dascopekey_${storageKey}`, scopeKey)
      return scopeKey
    })
    setCurrentScopeValues([])
  },
  [setCurrentScopeKey, storageKey, setCurrentScopeValues])

  const providerValue = useMemo(() => ({
    scopeKey: currentScopeKey,
    selectScopeKey,
    scopes: _scopes,
    selectedValues: currentScopeValues,
    selectScopeValues
  }), [_scopes, currentScopeKey, selectScopeKey, currentScopeValues, selectScopeValues])

  return (
    <DenaliScopeContext.Provider value={providerValue}>
      {children}
    </DenaliScopeContext.Provider>
  )
}

DenaliScopeProvider.propTypes = {
  /** Determines where we in local storage we store the last selected value */
  storageKey: PropTypes.string,

  /** determines the configuration of the available scopes and how they should be provided to underlying denali
   * reports */
  scopes: PropTypes.arrayOf(PropTypes.shape({
    /** The name of the scope */
    name: PropTypes.string,
    /** The path of group types to traverse */
    path: PropTypes.arrayOf(PropTypes.number),

    /** Values to be shown for the scope on <DAScopePicker> */
    labels: PropTypes.shape({
      subTitle: PropTypes.string,
      allItems: PropTypes.string,
      allMyItems: PropTypes.string,
      multipleItems: PropTypes.string
    }),

    /** Name of an icon in the icon system to show with the scope in the <DAScopeTypePicker> */
    icon: PropTypes.string
  })),

  /** Child components */
  children: PropTypes.node
}

DenaliScopeProvider.defaultProps = {
  storageKey: 'default'
}
